import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Radar, ShieldCheck, Loader2 } from "lucide-react";
import { sr, auth } from "../../lib/sr";

export default function SmartReachCredit() {
  const { code } = useParams();
  const nav = useNavigate();
  const [promo, setPromo] = useState(null);
  const [err, setErr] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const loggedIn = !!auth.getToken?.() || !!localStorage.getItem("tr_token");

  useEffect(() => {
    sr.ppPublicLookup(code).then(setPromo).catch((e) => setErr(e?.response?.data?.detail || "This credit code is not valid."));
  }, [code]);

  const doClaim = async () => {
    setClaiming(true);
    try {
      const r = await sr.creditClaim(code);
      setClaimed(r);
      toast.success("Credit added to your SmartReach account");
    } catch (e) {
      const d = e?.response?.data?.detail || "Could not claim this credit.";
      if (e?.response?.status === 401) { localStorage.setItem("pending_platform_credit", code); nav(`/login?next=/smartreach-credit/${code}`); return; }
      toast.error(d);
    } finally { setClaiming(false); }
  };

  const goAuth = (mode) => { localStorage.setItem("pending_platform_credit", code); nav(`/${mode}?next=/smartreach-credit/${code}`); };

  // auto-claim after login handoff
  useEffect(() => {
    if (promo && loggedIn && localStorage.getItem("pending_platform_credit") === code && !claimed) {
      localStorage.removeItem("pending_platform_credit");
      doClaim();
    }
    // eslint-disable-next-line
  }, [promo, loggedIn]);

  return (
    <div style={{ minHeight: "100vh", background: "#0A121C", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} data-testid="platform-credit-claim">
      <div style={{ width: 480, maxWidth: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 9, fontWeight: 800, fontSize: 20, marginBottom: 20 }}>
          <span style={{ width: 32, height: 32, borderRadius: 6, background: "linear-gradient(135deg,#C77F1A,#F2A33D)", display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#0A121C" }}><Radar size={18} /></span>
          SmartReach<span style={{ color: "#F2A33D" }}>AI</span>
        </div>
        {err && <div className="sr-card" style={{ padding: 24, color: "#0A121C" }} data-testid="credit-error">{err}</div>}
        {!err && !promo && <div style={{ color: "#93a1b3" }}><Loader2 className="sr-spin" size={18} /> Loading…</div>}
        {promo && (
          <div style={{ background: "#101C2C", border: "1px solid #1E2E40", borderRadius: 14, padding: 28 }}>
            {claimed ? (
              <div data-testid="credit-claimed">
                <div style={{ color: "#7ee2a8", display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}><ShieldCheck size={18} /> Credit added</div>
                <div style={{ fontSize: 40, fontWeight: 800, color: "#F2A33D", marginTop: 14 }}>{claimed.display_value}</div>
                <p style={{ color: "#93a1b3" }}>{claimed.name} is ready in your SmartReach Credits.</p>
                <button className="sr-btn sr-btn-primary" onClick={() => nav("/app/campaigns/new")} data-testid="credit-build-campaign" style={{ marginTop: 12 }}>Build a campaign</button>
              </div>
            ) : (
              <>
                <div style={{ color: "#F2A33D", fontSize: 12, letterSpacing: ".08em" }}>YOU'VE RECEIVED SMARTREACH MARKETING CREDIT</div>
                <div style={{ fontSize: 46, fontWeight: 800, color: "#F2A33D", marginTop: 12 }} data-testid="credit-value">{promo.display_value}</div>
                <div style={{ fontSize: 16, fontWeight: 600, marginTop: 4 }}>{promo.name}</div>
                <p style={{ color: "#93a1b3", fontSize: 14 }}>{promo.description}</p>
                <div style={{ fontSize: 13, color: "#93a1b3", margin: "12px 0" }}>
                  {promo.minimum_spend ? <div>Minimum campaign spend: ${Number(promo.minimum_spend).toLocaleString()}</div> : null}
                  {promo.expires_at ? <div>Expires {new Date(promo.expires_at).toLocaleDateString()}</div> : null}
                  <div>Code: <span style={{ fontFamily: "monospace" }}>{promo.code}</span></div>
                </div>
                {loggedIn ? (
                  <button className="sr-btn sr-btn-primary" onClick={doClaim} disabled={claiming} data-testid="credit-claim-btn" style={{ width: "100%" }}>
                    {claiming ? <Loader2 size={15} className="sr-spin" /> : null} Claim this credit
                  </button>
                ) : (
                  <div style={{ display: "grid", gap: 10 }}>
                    <button className="sr-btn sr-btn-primary" onClick={() => goAuth("signup")} data-testid="credit-signup-btn" style={{ width: "100%" }}>Create my account &amp; claim</button>
                    <button className="sr-btn" onClick={() => goAuth("login")} data-testid="credit-login-btn" style={{ width: "100%" }}>I already have SmartReach</button>
                  </div>
                )}
                <p style={{ color: "#5b6b7d", fontSize: 11, marginTop: 14 }}>SmartReach promotional credit has no cash value and may only be used toward eligible SmartReach products or services according to the promotion terms.</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
